import { Formik } from 'formik';
import { Button, Col, Field, Row, Step } from 'octiv-components';
import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

export default ({ isLoading, initialValues = {}, ...props }) => {
  const { t } = useTranslation();
  const isCreate = JSON.stringify(initialValues) === JSON.stringify({});

  return (
    <Formik
      {...props}
      initialValues={{
        name: initialValues.name || undefined,
        description: initialValues.description || undefined,
        price: initialValues.price || undefined,
        healthProviderPrice: initialValues?.healthProviderPrice || undefined,
        priority: initialValues.priority || undefined,
        limit: initialValues.limit || undefined,
        isFree: initialValues.isFree === 1,
        purchases: initialValues.settings?.limits?.purchases || undefined,
        purchasesPerUser:
          initialValues.settings?.limits?.purchasesPerUser || undefined,
      }}
      validationSchema={Yup.object().shape({
        name: Yup.string().required(t('required')),
        description: Yup.string().required(t('required')),
        price: Yup.number().when('isFree', {
          is: true,
          then: Yup.number().oneOf([0], t('mustBeZeroWhenFree')),
          otherwise: Yup.number().required(t('required')),
        }),
        healthProviderPrice: Yup.string().when('isFree', {
          is: true,
          then: Yup.string().nullable(),
          otherwise: Yup.string(),
        }),
        priority: Yup.string(),
        limit: Yup.number()
          .required(t('required'))
          .positive(t('mustBeGreaterThanZero'))
          .moreThan(0, t('mustBeGreaterThanZero')),
        isFree: Yup.bool().required(t('required')),
        purchases: Yup.number().typeError(t('mustBeANumber')).nullable(),
        purchasesPerUser: Yup.number().typeError(t('mustBeANumber')).nullable(),
      })}
    >
      {({
        handleSubmit,
        setFieldValue,
        dirty,
        values: { isFree, healthProviderPrice },
      }) => (
        <form onSubmit={handleSubmit}>
          <Step
            hasBorder
            boxContainerProps={{ width: '100%', my: 4 }}
            title={t('packageDetails')}
            value='1'
          >
            <Row>
              <Col>
                <Field label={t('name')} name='name' />
              </Col>

              <Col>
                <Field
                  as='textarea'
                  label={t('description')}
                  name='description'
                />
              </Col>

              <Col>
                <Field
                  hasAlternateBackground
                  isHorizontal
                  isRadio
                  doFinally={() => {
                    setFieldValue('price', isFree ? 0 : undefined);
                    setFieldValue('purchases', null);
                    setFieldValue('purchasesPerUser', null);
                  }}
                  isDisabled={!isCreate}
                  label={t('isThisPackageFree')}
                  name='isFree'
                  options={[
                    { label: t('no'), value: false },
                    { label: t('yes'), value: true },
                  ]}
                />
              </Col>
              <Col>
                <Field
                  isDisabled={isFree}
                  label={t('price')}
                  name='price'
                  placeholder={isFree ? '0' : undefined}
                />
              </Col>

              {props?.isHealthProvider && !isFree && (
                <Col>
                  <Field
                    label={t('healthProviderPrice')}
                    name='healthProviderPrice'
                  />
                </Col>
              )}

              <Col>
                <Field
                  isDisabled={!isCreate}
                  label={t('sessions')}
                  min={1}
                  name='limit'
                  type='number'
                  onWheel={(e) => e.target.blur()}
                />
              </Col>

              <Col>
                <Field
                  helper={t('usePriorityToSortPackagesOnDropInWidget')}
                  label={t('priority')}
                  name='priority'
                />
              </Col>
            </Row>
          </Step>

          {!healthProviderPrice && (
            <Step
              hasBorder
              boxContainerProps={{ width: '100%', my: 4 }}
              title={t('bookingLimits')}
              value='2'
            >
              <Row>
                <Col>
                  <Field
                    label={t('totalPurchasesAvailable')}
                    name='purchases'
                  />
                </Col>

                <Col>
                  <Field
                    label={t('purchasesPerUser')}
                    name='purchasesPerUser'
                  />
                </Col>
              </Row>
            </Step>
          )}
          <Row>
            <Col>
              <Button
                isDisabled={!dirty}
                isLoading={isLoading}
                text={t('submit')}
                type='submit'
              />
            </Col>
          </Row>
        </form>
      )}
    </Formik>
  );
};
